import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaFilter, FaTimes, FaListUl, FaChevronDown } from 'react-icons/fa';
import AutomovelBox from '../Components/AutomovelBox';
import AutomoveisGrid from '../Components/AutomoveisGrid';
import LoadingSpinner from '../Components/LoadingSpinner';
import { useCar } from '../stores/CarContext';
import SEO from '../Components/SEO';
import { buildBreadcrumbSchema, buildItemListSchema, PAGE_SEO } from '../seoConfig';

const carWeights = new Map();
const getCarWeight = (carId) => {
    if (!carWeights.has(carId)) {
        carWeights.set(carId, Math.random());
    }
    return carWeights.get(carId);
};

const AutomoveisLista = () => {
    const { cars, fetchCarsAU, isLoading } = useCar();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterMarca, setFilterMarca] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    useEffect(() => { fetchCarsAU(); }, [fetchCarsAU]);

    const sortedCars = useMemo(() => {
        if (!cars || cars.length === 0) return [];
        return [...cars].sort((a, b) => getCarWeight(a.id) - getCarWeight(b.id));
    }, [cars]);

    const marcas = [...new Set(cars.map(c => c.marca))].sort();

    const filteredCars = useMemo(() => {
        return sortedCars.filter(car => {
            const matchesSearch = car.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                 car.marca.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesMarca = filterMarca === '' || car.marca === filterMarca;
            return matchesSearch && matchesMarca;
        });
    }, [sortedCars, searchTerm, filterMarca]);

    const filterBtn = (active) =>
        `px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-200 border ${
            active
                ? 'bg-red text-black border-red'
                : 'bg-transparent border-white/10 text-white/40 hover:border-red/30 hover:text-white'
        }`;

    return (
        <div className="bg-black min-h-screen text-white overflow-x-hidden">
            <SEO
                title={PAGE_SEO.stock.title}
                description={PAGE_SEO.stock.description}
                path={PAGE_SEO.stock.path}
                image={PAGE_SEO.stock.image}
                structuredData={[
                    buildBreadcrumbSchema([
                        { name: 'Início', path: '/' },
                        { name: 'Stock de Veículos', path: '/automoveis' },
                    ]),
                    buildItemListSchema(cars),
                ].filter(Boolean)}
            />

            <div className="ajp-section-dark pt-28 pb-14 px-5 lg:px-12 relative overflow-hidden border-b border-white/[0.06]">
                <div className="ajp-grain absolute inset-0" />
                <div className="relative z-10 max-w-[1500px] mx-auto">
                    <p className="section-tag mb-4">Showroom</p>
                    <h1 className="font-display text-6xl sm:text-8xl text-white tracking-wide leading-none mb-4">
                        NOSSO <span className="text-gradient">STOCK</span>
                    </h1>
                    <p className="text-white/40 text-sm">
                        <span className="text-red font-bold">{cars.length}</span> veículos disponíveis
                    </p>
                </div>
            </div>

            <div className="sticky top-[72px] z-50 bg-black/95 backdrop-blur-md border-b border-white/[0.06]">
                <div className="max-w-[1500px] mx-auto px-5 lg:px-12">
                    <div className="flex items-center gap-3 py-4">
                        <div className="flex-1 flex items-center gap-3 border border-white/10 focus-within:border-red px-4 py-3 transition-all">
                            <FaSearch className="text-white/20 shrink-0" size={12} />
                            <input
                                type="text"
                                placeholder="Pesquisar marca ou modelo..."
                                className="bg-transparent text-sm font-medium outline-none text-white placeholder-white/20 w-full"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="text-white/30 hover:text-red transition-colors">
                                    <FaTimes size={10} />
                                </button>
                            )}
                        </div>

                        <button
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className={`flex items-center gap-2 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.15em] border transition-all ${
                                isFilterOpen || filterMarca
                                    ? 'bg-red text-black border-red'
                                    : 'border-white/10 text-white/40 hover:border-red/30'
                            }`}
                        >
                            <FaFilter size={9} />
                            Filtrar
                            {filterMarca && <span className="bg-black text-red text-[9px] w-4 h-4 flex items-center justify-center">1</span>}
                            <FaChevronDown size={8} className={`transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
                        </button>

                        <div className="hidden md:block text-[10px] font-bold uppercase tracking-[0.15em] text-white/30 shrink-0">
                            <span className="text-red">{filteredCars.length}</span> resultados
                        </div>
                    </div>

                    <div className={`overflow-hidden transition-all duration-300 ${isFilterOpen ? 'max-h-40 pb-4' : 'max-h-0'}`}>
                        <div className="flex flex-wrap gap-2">
                            <button onClick={() => setFilterMarca('')} className={filterBtn(filterMarca === '')}>
                                Todas
                            </button>
                            {marcas.map(m => (
                                <button key={m} onClick={() => setFilterMarca(m)} className={filterBtn(filterMarca === m)}>
                                    {m}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="ajp-section-alt max-w-[1500px] mx-auto px-5 lg:px-12 py-12">
                {isLoading ? (
                    <div className="py-40 flex justify-center"><LoadingSpinner /></div>
                ) : filteredCars.length > 0 ? (
                    <AutomoveisGrid>
                        {filteredCars.map((carro, i) => (
                            <div
                                key={carro.id || i}
                                className="animate-fade-up"
                                style={{ animationDelay: `${Math.min(i, 8) * 50}ms`, opacity: 0, animationFillMode: 'forwards' }}
                            >
                                <AutomovelBox
                                    carId={carro.id}
                                    carIndex={i}
                                    carImage={carro.thumb_url}
                                    title={carro.titulo}
                                    price={carro.preco}
                                    kms={carro.kms}
                                    year={carro.ano}
                                    vendido={carro.vendido}
                                    brevemente={carro.brevemente}
                                    instagramShortcode={carro.instagramShortcode}
                                />
                            </div>
                        ))}
                    </AutomoveisGrid>
                ) : (
                    <div className="py-32 text-center space-y-6">
                        <div className="w-16 h-16 border border-white/10 flex items-center justify-center mx-auto">
                            <FaListUl size={20} className="text-white/20" />
                        </div>
                        <div>
                            <h3 className="font-display text-3xl text-white tracking-widest mb-2">SEM RESULTADOS</h3>
                            <p className="text-white/30 text-sm">Tenta outra pesquisa ou limpa os filtros.</p>
                        </div>
                        <button onClick={() => { setSearchTerm(''); setFilterMarca(''); }} className="btn-primary mx-auto">
                            Limpar filtros
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AutomoveisLista;
